import renderMathInElement from "katex/dist/contrib/auto-render";
import { useRef, useState, useEffect } from "react";
import api from "../services/api";
import { getCourses, getCurriculum, getTeacherCourses } from "../services/courses";
import { createExam, getCourseExams, updateExam, deleteExam, getAllExams } from "../services/exams";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";
import Sidebar from "../components/Sidebar";
import {
  Plus,
  Trash2,
  ClipboardCheck,
  Edit2,
  X,
  Download,
  Sparkles,
  Loader2,
  FileText,
  UploadCloud
} from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';
import Papa from "papaparse";
import * as mammoth from "mammoth";



const createBlankExamQuestion = (groupName = "", sectionName = "") => ({
  group_name: groupName,
  section_name: sectionName,
  question_type: "Multiple Choice Question",
  linked_prompt: "",
  cannot_edit_response: false,
  cannot_revisit: false,
  allow_calculator: false,
  prompt: "",
  options: [{ id: "A", text: "" }, { id: "B", text: "" }, { id: "C", text: "" }, { id: "D", text: "" }],
  option_a: "",
  option_b: "",
  option_c: "",
  option_d: "",
  correct_option: "A",
  explanation: "",
  marks: 1,
  negative_marks: 0.5,
  is_mandatory: false,
});


function MathPreview({ html }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      try {
        renderMathInElement(ref.current, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "\\(", right: "\\)", display: false },
            { left: "$", right: "$", display: false }
          ],
          throwOnError: false
        });
      } catch (err) {}
    }
  }, [html]);
  return <div ref={ref} className="prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: html }} />;
}

function RichExamField({ label, value, onChange, onUpload }) {
  const textareaRef = useRef(null);

  const wrapSelection = (before, after = before) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.slice(start, end);
    const next = `${value.slice(0, start)}${before}${selected || label}${after}${value.slice(end)}`;
    onChange(next);
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
      <div className="flex flex-col gap-px bg-slate-200 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-800">
        <div className="flex flex-wrap items-center gap-2 bg-slate-100 dark:bg-slate-900 px-3 py-2">
          <span className="text-xs font-black uppercase text-slate-500 mr-auto">{label}</span>
          <button type="button" title="Bold" onClick={() => wrapSelection("<b>", "</b>")} className="px-2 py-1 text-xs font-black bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 rounded shadow-sm">B</button>
          <button type="button" title="Italic" onClick={() => wrapSelection("<i>", "</i>")} className="px-2 py-1 text-xs italic font-bold bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 rounded shadow-sm">I</button>
          <button type="button" title="Underline" onClick={() => wrapSelection("<u>", "</u>")} className="px-2 py-1 text-xs underline font-bold bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 rounded shadow-sm">U</button>
          <div className="w-px h-4 bg-slate-300 dark:bg-slate-700 mx-1"></div>
          <button type="button" title="Inline Math Block" onClick={() => wrapSelection("\\(", "\\)")} className="px-2 py-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-white dark:bg-slate-950 border border-indigo-200 dark:border-indigo-900 hover:bg-indigo-50 rounded shadow-sm">\( x \)</button>
          <button type="button" title="Display Math Block" onClick={() => wrapSelection("$$", "$$")} className="px-2 py-1 text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-white dark:bg-slate-950 border border-indigo-200 dark:border-indigo-900 hover:bg-indigo-50 rounded shadow-sm">$$ X $$</button>
          <div className="w-px h-4 bg-slate-300 dark:bg-slate-700 mx-1"></div>
          <label className="px-2 py-1 text-xs font-bold bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 rounded cursor-pointer shadow-sm">
            Image
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { onUpload(e.target.files[0], "image"); e.target.value = null; }} />
          </label>
          <label className="px-2 py-1 text-xs font-bold bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 rounded cursor-pointer shadow-sm">
            Video
            <input type="file" accept="video/*" className="hidden" onChange={(e) => { onUpload(e.target.files[0], "video"); e.target.value = null; }} />
          </label>
        </div>
        
        {/* Math Function Templates Toolbar */}
        <div className="flex flex-wrap items-center gap-1.5 bg-indigo-50/50 dark:bg-indigo-950/20 px-3 py-1.5">
          <span className="text-[10px] font-bold uppercase text-indigo-600 dark:text-indigo-400 mr-2">Math Templates:</span>
          <button type="button" title="Fraction" onClick={() => wrapSelection("\\frac{", "}{}")} className="px-2 py-0.5 text-xs font-bold bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-900 hover:border-indigo-400 hover:bg-indigo-50 rounded flex flex-col items-center leading-none"><span>a</span><span className="border-t border-black dark:border-white w-3"></span><span>b</span></button>
          <button type="button" title="Square Root" onClick={() => wrapSelection("\\sqrt{", "}")} className="px-2 py-0.5 text-xs font-bold bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-900 hover:border-indigo-400 hover:bg-indigo-50 rounded flex items-center">√x</button>
          <button type="button" title="Power / Exponent" onClick={() => wrapSelection("^{", "}")} className="px-2 py-0.5 text-xs font-bold bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-900 hover:border-indigo-400 hover:bg-indigo-50 rounded flex items-start">x<sup>2</sup></button>
          <button type="button" title="Subscript" onClick={() => wrapSelection("_{", "}")} className="px-2 py-0.5 text-xs font-bold bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-900 hover:border-indigo-400 hover:bg-indigo-50 rounded flex items-end">x<sub>n</sub></button>
          <button type="button" title="Integration" onClick={() => wrapSelection("\\int ", "")} className="px-2 py-0.5 text-xs italic bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-900 hover:border-indigo-400 hover:bg-indigo-50 rounded font-serif text-base leading-none">∫</button>
          <button type="button" title="Summation" onClick={() => wrapSelection("\\sum_{}^{}", "")} className="px-2 py-0.5 text-xs bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-900 hover:border-indigo-400 hover:bg-indigo-50 rounded text-base leading-none">∑</button>
          <button type="button" title="Pi" onClick={() => wrapSelection("\\pi", "")} className="px-2 py-0.5 text-xs bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-900 hover:border-indigo-400 hover:bg-indigo-50 rounded font-serif text-sm leading-none">π</button>
          <button type="button" title="Theta" onClick={() => wrapSelection("\\theta", "")} className="px-2 py-0.5 text-xs bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-900 hover:border-indigo-400 hover:bg-indigo-50 rounded font-serif text-sm leading-none">θ</button>
        </div>
      </div>
      <textarea
        ref={textareaRef}
        className="w-full min-h-24 px-4 py-3 bg-white dark:bg-slate-950 outline-none text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`${label} text, image, or video`}
      />
      {value && (
        <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-3 text-sm bg-slate-50 dark:bg-slate-900">
          <div className="text-[10px] font-black uppercase text-slate-400 mb-2">Preview</div>
          <MathPreview html={value} />
        </div>
      )}
    </div>
  );
}

export default function TeacherExams({ defaultCourseId, isEmbedded }) {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  const [courseExams, setCourseExams] = useState([]);
  const [examTitle, setExamTitle] = useState("");
  const [examCategory, setExamCategory] = useState("");
  const [examDuration, setExamDuration] = useState(30);
  const [examPassingScore, setExamPassingScore] = useState(60);
  const [examGroups, setExamGroups] = useState([]);
  const [examSections, setExamSections] = useState({});
  const [activeExamGroup, setActiveExamGroup] = useState("");
  const [activeExamSection, setActiveExamSection] = useState("");
  const [newExamGroup, setNewExamGroup] = useState("");
  const [newExamSection, setNewExamSection] = useState("");
  const [examQuestions, setExamQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(createBlankExamQuestion("", ""));
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [editingExamId, setEditingExamId] = useState(null);

  const [curriculum, setCurriculum] = useState([]);
  const [aiModule, setAiModule] = useState("");
  const [aiSection, setAiSection] = useState("");
  const [aiTopic, setAiTopic] = useState("");
  const [aiGenCount, setAiGenCount] = useState(5);
  const [aiDifficulty, setAiDifficulty] = useState("Medium");
  const [aiProvider, setAiProvider] = useState("nvidia");
  const [aiQuestionType, setAiQuestionType] = useState("Multiple Choice Question");
  const [aiCognitiveLevel, setAiCognitiveLevel] = useState("Application");
  const [generatingAI, setGeneratingAI] = useState(false);

  useEffect(() => {
    const init = async () => {
      const token = getToken();
      if (!token) return;
      const user = jwtDecode(token);

      try {
        const res = await getTeacherCourses();
        const myCourses = res.data;
        setCourses(myCourses);
        if (myCourses.length > 0) {
          const firstCourseId = defaultCourseId ? Number(defaultCourseId) : myCourses[0].id;
          setSelectedCourse(firstCourseId);
          loadCourseExams(firstCourseId);
        }
      } catch (err) {
        toast.error("Failed to load courses");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const groups = new Set();
    const sections = {};
    examQuestions.forEach(q => {
      if (q.group_name) {
        groups.add(q.group_name);
        if (!sections[q.group_name]) sections[q.group_name] = new Set();
        if (q.section_name) sections[q.group_name].add(q.section_name);
      }
    });
    setExamGroups(Array.from(groups));
    const sectionsObj = {};
    Object.keys(sections).forEach(k => sectionsObj[k] = Array.from(sections[k]));
    setExamSections(sectionsObj);
  }, [examQuestions]);


  const loadCourseExams = async (courseId) => {
    try {
      const res = courseId && courseId !== "all"
        ? await getCourseExams(courseId)
        : await getAllExams();
      setCourseExams(res.data);

      if (courseId && courseId !== "all") {
        const curRes = await getCurriculum(courseId);
        setCurriculum(curRes.data || []);
      } else {
        setCurriculum([]);
      }
    } catch (err) {
      console.error("Failed to load exams/curriculum");
    }
  };

  const handleCourseChange = (e) => {
    const id = e.target.value === "all" ? "all" : Number(e.target.value);
    setSelectedCourse(id);
    loadCourseExams(id);
  };

  const updateCurrentQuestion = (field, value) => {
    setCurrentQuestion(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveCurrentQuestion = () => {
    const q = { ...currentQuestion };

    if (!q.group_name) return toast.error("Select or enter a group");
    if (!q.prompt.trim()) {
      return toast.error("Fill in the question prompt");
    }
    if (q.question_type === "Multiple Choice Question") {
      if (!q.options || q.options.length < 2) return toast.error("Provide at least 2 options");
      if (q.options.some(opt => !opt.id.trim() || !opt.text.trim())) return toast.error("Fill in all option labels and texts");
    }

    if (editingQuestionIndex !== null) {
      setExamQuestions(items => items.map((item, idx) => idx === editingQuestionIndex ? q : item));
      setEditingQuestionIndex(null);
    } else {
      setExamQuestions(items => [...items, q]);
    }

    setCurrentQuestion(createBlankExamQuestion(q.group_name, q.section_name));
    toast.success(editingQuestionIndex !== null ? "Question updated in list" : "Question saved to list");
  };

  const editQuestionFromList = (index) => {
    setCurrentQuestion(JSON.parse(JSON.stringify(examQuestions[index])));
    setEditingQuestionIndex(index);
  };

  const updateCurrentQuestionOption = (index, field, value) => {
    setCurrentQuestion(prev => {
      const newOpts = [...(prev.options || [])];
      newOpts[index] = { ...newOpts[index], [field]: value };
      return { ...prev, options: newOpts };
    });
  };

  const handleEditExam = (exam) => {
    setEditingExamId(exam.id);
    setExamTitle(exam.title);
    setExamCategory(exam.category || "");
    setExamDuration(exam.duration_minutes);
    setExamPassingScore(exam.passing_score);
    setSelectedCourse(exam.course_id);

    setExamQuestions(exam.questions.map(q => ({
      group_name: q.group_name || "",
      section_name: q.section_name || "",
      question_type: q.question_type || "Multiple Choice Question",
      linked_prompt: q.linked_prompt || "",
      is_mandatory: q.is_mandatory ?? false,
      cannot_edit_response: q.cannot_edit_response || false,
      cannot_revisit: q.cannot_revisit || false,
      allow_calculator: q.allow_calculator || false,
      prompt: q.prompt || "",
      options: q.options && q.options.length > 0 ? q.options : [
        { id: "A", text: q.option_a || "" },
        { id: "B", text: q.option_b || "" },
        { id: "C", text: q.option_c || "" },
        { id: "D", text: q.option_d || "" }
      ].filter(o => o.text),
      option_a: q.option_a || "",
      option_b: q.option_b || "",
      option_c: q.option_c || "",
      option_d: q.option_d || "",
      correct_option: q.correct_option || "A",
      marks: q.marks || 1,
      negative_marks: q.negative_marks || 0.5,
    })));
  };

  const cancelEdit = () => {
    setEditingExamId(null);
    setExamTitle("");
    setExamCategory("");
    setExamDuration(30);
    setExamPassingScore(60);
    setExamGroups([]);
    setExamSections({});
    setActiveExamGroup("");
    setActiveExamSection("");
    setExamQuestions([]);
    setCurrentQuestion(createBlankExamQuestion("", ""));
    setEditingQuestionIndex(null);
  };

  const handleDeleteExam = async (examId) => {
    if (!window.confirm("Are you sure you want to delete this exam?")) return;
    try {
      await deleteExam(examId);
      toast.success("Exam deleted");
      if (editingExamId === examId) cancelEdit();
      loadCourseExams(selectedCourse);
    } catch (err) {
      toast.error("Failed to delete exam");
    }
  };

  const downloadSampleCSV = () => {
    const header = "group,section,type,marks,negative_marks,mandatory,cannot_edit,cannot_revisit,allow_calculator,question,answer,option_A,option_B,option_C,option_D\n";
    const row = 'General Knowledge,Section 1,Multiple Choice Question,1,0.5,yes,no,no,no,"What is the capital of France?",A,Paris,London,Berlin,Rome\n';
    const blob = new Blob([header + row], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_questions.csv";
    a.click();
  };

  const downloadSampleWordText = () => {
    const content = `Group: General Knowledge
Section: Section 1
Type: Multiple Choice Question
Marks: 1
Negative Marks: 0.5
Mandatory: No
Cannot Edit: No
Cannot Revisit: No
Allow Calculator: No
Question: What is the capital of France?
A: Paris
B: London
C: Berlin
D: Rome
Answer: A
`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_questions.txt";
    a.click();
  };

  const processParsedData = (data, format) => {
    const newQuestions = [];
    const newGroups = new Set(examGroups);
    const newSections = { ...examSections };

    if (format === "csv") {
      data.forEach(row => {
        if (!row.question || !row.question.trim()) return;

        const q = createBlankExamQuestion(row.group || "Default Group", row.section || "");
        q.question_type = row.type || "Multiple Choice Question";
        q.marks = Number(row.marks) || 1;
        q.negative_marks = Number(row.negative_marks) || 0;
        q.prompt = row.question;
        q.correct_option = (row.answer || "A").trim().toUpperCase();
        q.is_mandatory = String(row.mandatory).toLowerCase() !== "no" && String(row.mandatory).toLowerCase() !== "false";
        q.cannot_edit_response = String(row.cannot_edit).toLowerCase() === "yes" || String(row.cannot_edit).toLowerCase() === "true";
        q.cannot_revisit = String(row.cannot_revisit).toLowerCase() === "yes" || String(row.cannot_revisit).toLowerCase() === "true";
        q.allow_calculator = String(row.allow_calculator).toLowerCase() === "yes" || String(row.allow_calculator).toLowerCase() === "true";

        const options = [];
        ['A', 'B', 'C', 'D', 'E', 'F'].forEach(letter => {
          const optText = row[`option_${letter}`] || row[`Option ${letter}`] || row[letter];
          if (optText) options.push({ id: letter, text: String(optText).trim() });
        });

        if (q.question_type === "Multiple Choice Question" && options.length > 0) {
          q.options = options;
        }

        newQuestions.push(q);
      });
    } else if (format === "text") {
      const blocks = data.split(/\n\s*\n/);
      blocks.forEach(block => {
        const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
        if (lines.length === 0) return;

        const q = createBlankExamQuestion("Default Group", "");
        let options = [];
        let isQuestionText = false;

        lines.forEach(line => {
          const lower = line.toLowerCase();
          if (lower.startsWith("group:")) q.group_name = line.substring(6).trim();
          else if (lower.startsWith("section:")) q.section_name = line.substring(8).trim();
          else if (lower.startsWith("type:")) q.question_type = line.substring(5).trim();
          else if (lower.startsWith("marks:")) q.marks = Number(line.substring(6).trim()) || 1;
          else if (lower.startsWith("negative marks:")) q.negative_marks = Number(line.substring(15).trim()) || 0;
          else if (lower.startsWith("mandatory:")) q.is_mandatory = !lower.includes("yes");
          else if (lower.startsWith("cannot edit:")) q.cannot_edit_response = lower.includes("yes");
          else if (lower.startsWith("cannot revisit:")) q.cannot_revisit = lower.includes("yes");
          else if (lower.startsWith("allow calculator:")) q.allow_calculator = lower.includes("yes");
          else if (lower.startsWith("question:")) {
            q.prompt = line.substring(9).trim();
            isQuestionText = true;
          }
          else if (lower.startsWith("answer:")) {
            q.correct_option = line.substring(7).trim().toUpperCase();
            isQuestionText = false;
          }
          else if (/^[a-f]:/i.test(line)) {
            const letter = line.charAt(0).toUpperCase();
            options.push({ id: letter, text: line.substring(2).trim() });
            isQuestionText = false;
          }
          else if (isQuestionText) {
            q.prompt += "\n" + line;
          }
        });

        if (!q.prompt) return;

        if (q.question_type === "Multiple Choice Question" && options.length > 0) {
          q.options = options;
        }

        newQuestions.push(q);
      });
    }

    if (newQuestions.length === 0) {
      return toast.error("No valid questions found in file");
    }

    newQuestions.forEach(q => {
      newGroups.add(q.group_name);
      if (!newSections[q.group_name]) newSections[q.group_name] = [];
      if (q.section_name && !newSections[q.group_name].includes(q.section_name)) {
        newSections[q.group_name].push(q.section_name);
      }
    });

    setExamGroups(Array.from(newGroups));
    setExamSections(newSections);
    if (!activeExamGroup && newQuestions.length > 0) setActiveExamGroup(newQuestions[0].group_name);

    setExamQuestions(prev => [...prev, ...newQuestions]);
    toast.success(`Successfully imported ${newQuestions.length} questions!`);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      if (file.name.endsWith(".csv")) {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => processParsedData(results.data, "csv")
        });
      } else if (file.name.endsWith(".docx")) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        processParsedData(result.value, "text");
      } else if (file.name.endsWith(".txt")) {
        const text = await file.text();
        processParsedData(text, "text");
      } else {
        toast.error("Unsupported file format. Please upload CSV, TXT, or DOCX.");
      }
    } catch (err) {
      toast.error("Error processing file");
    }
    e.target.value = null;
  };

  const handleGenerateAIQuestions = async () => {
    if (!aiModule.trim() && !aiSection.trim()) {
      return toast.error("Provide a module or section context for AI generation.");
    }
    setGeneratingAI(true);
    const toastId = toast.loading("Antigravity AI is crafting questions...");
    try {
      const res = await api.post("/ai/generate-questions", {
        module_name: aiModule || "General",
        section_name: aiSection || "Topic",
        topic: aiTopic,
        num_questions: Number(aiGenCount) || 5,
        difficulty: aiDifficulty,
        provider: aiProvider,
        question_type: aiQuestionType,
        cognitive_level: aiCognitiveLevel
      });

      const data = res.data.questions;
      if (!data || !data.length) throw new Error("AI returned no questions.");

      const newItems = data.map(q => ({
        ...createBlankExamQuestion(aiModule || "AI Module", aiSection || "AI Section"),
        prompt: q.prompt,
        question_type: q.question_type || aiQuestionType || "Multiple Choice Question",
        options: q.options && q.options.length > 0 ? q.options : [
          { id: "A", text: "" }, { id: "B", text: "" }, { id: "C", text: "" }, { id: "D", text: "" }
        ],
        correct_option: q.correct_option || "A",
        explanation: q.explanation || ""
      }));

      setExamQuestions(prev => [...prev, ...newItems]);
      toast.success(`Succesfully generated ${newItems.length} AI questions!`, { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI failed to generate questions.", { id: toastId });
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSyncKnowledgeBasePDF = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      return toast.error("Please upload a PDF file only.");
    }

    const loadingToast = toast.loading(`Antigravity AI is memorizing ${file.name}...`);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/ai/ingest-pdf", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data.success) {
        toast.success(`Success! File vectorized into Pinecone.`, { id: loadingToast, duration: 4000 });
      } else {
        toast.error(res.data.detail || "Failed to learn document.", { id: loadingToast });
      }
    } catch (err) {
      toast.error("Failed to sync document to AI memory.", { id: loadingToast });
    }
    e.target.value = null; // reset
  };

  const addExamGroup = () => {
    const group = newExamGroup.trim();
    if (!group) return;
    if (!examGroups.includes(group)) {
      setExamGroups((items) => [...items, group]);
      setExamSections((items) => ({ ...items, [group]: [] }));
    }
    setActiveExamGroup(group);
    setNewExamGroup("");
  };

  const addExamSection = () => {
    const section = newExamSection.trim();
    if (!section || !activeExamGroup) return toast.error("Select or create a group first");
    setExamSections((items) => {
      const currentSections = items[activeExamGroup] || [];
      return currentSections.includes(section)
        ? items
        : { ...items, [activeExamGroup]: [...currentSections, section] };
    });
    setActiveExamSection(section);
    setNewExamSection("");
  };

  const handleExamContentUpload = async (file, kind, questionIndex, field) => {
    if (!file) return;
    const loadingToast = toast.loading(`Uploading ${kind}...`);
    try {
      const fallbackType = kind === "image" ? "image/jpeg" : "video/mp4";
      const contentType = file.type || fallbackType;
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload/file", formData);
      const file_url = res.data.url;

      let extraStyles = "max-width:100%;border-radius:8px;margin:8px 0;";
      if (kind === "image") {
        const customWidth = window.prompt("Enter image width (e.g. 200px, 50%, or leave blank for default):");
        if (customWidth) extraStyles += `width:${customWidth};`;
        const customHeight = window.prompt("Enter image height (e.g. 200px, auto, or leave blank for default):");
        if (customHeight) extraStyles += `height:${customHeight};`;
      }

      const tag = kind === "image"
        ? `<img src="${file_url}" alt="${file.name}" style="${extraStyles}" />`
        : `<video src="${file_url}" controls controlsList="nodownload" style="${extraStyles}"></video>`;
      setCurrentQuestion((prev) => ({
        ...prev,
        [field]: `${prev[field] || ""}\n${tag}`
      }));
      toast.dismiss(loadingToast);
      toast.success(`${kind === "image" ? "Image" : "Video"} added`);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Upload failed");
    }
  };

  const removeExamQuestion = (index) => {
    setExamQuestions((items) => items.filter((_, itemIndex) => itemIndex !== index));
    if (editingQuestionIndex === index) {
      setEditingQuestionIndex(null);
      setCurrentQuestion(createBlankExamQuestion(activeExamGroup, activeExamSection));
    }
  };

  const handleCreateExam = async () => {
    if (!selectedCourse || !examTitle.trim()) return toast.error("Enter exam title");
    const validQuestions = examQuestions.filter((q) => {
      if (!q.prompt.trim()) return false;
      if (q.question_type === "Multiple Choice Question") {
        if (!q.options || q.options.length < 2) return false;
        if (q.options.some(opt => !opt.id.trim() || !opt.text.trim())) return false;
      }
      return true;
    });
    if (validQuestions.length === 0 && currentQuestion.prompt.trim()) {
      return toast.error("You have an unsaved question. Please 'Save Question to List' first.");
    }
    if (validQuestions.some(q => !q.group_name)) return toast.error("Please assign a group to all questions");

    try {
      const payload = {
        title: examTitle,
        category: examCategory,
        duration_minutes: Number(examDuration) || 30,
        passing_score: Number(examPassingScore) || 60,
        questions: validQuestions,
      };

      if (editingExamId) {
        await updateExam(editingExamId, payload);
        toast.success("Exam updated!");
      } else {
        await createExam(selectedCourse, payload);
        toast.success("Exam published!");
      }

      loadCourseExams(selectedCourse);

      // Reset forms and selections fully
      setExamTitle("");
      setExamCategory("");
      setExamDuration(30);
      setExamPassingScore(60);
      setExamGroups([]);
      setExamSections({});
      setActiveExamGroup("");
      setActiveExamSection("");
      setExamQuestions([]);
      setCurrentQuestion(createBlankExamQuestion("", ""));
      setEditingQuestionIndex(null);
      setEditingExamId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save exam");
    }
  };

  if (loading) return (
    <div className="flex h-screen items-center justify-center dark:bg-slate-900">
      <div className="w-10 h-10 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className={isEmbedded ? "w-full" : "min-h-screen bg-slate-50 dark:bg-slate-950 flex"}>
      {!isEmbedded && <Toaster position="top-center" />}
      {!isEmbedded && <Sidebar role="teacher" />}

      <main className={`flex-1 w-full space-y-10 max-w-5xl mx-auto ${isEmbedded ? "" : "lg:ml-64 p-6 lg:p-10"}`}>
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white">Publish Exams</h1>
            <p className="text-slate-500 dark:text-slate-400">Manage exams and upload questions for your courses.</p>
          </div>
        </header>

        {!isEmbedded && (
        <section className="card p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-blue-600">
            <ClipboardCheck size={20} /> Select Course
          </h3>
          <select
            className="w-full max-w-md px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border dark:border-slate-800 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20"
            onChange={handleCourseChange}
            value={selectedCourse || ""}
          >
            {selectedCourse === "all" && <option value="all">All Courses</option>}
            <option value="all">-- View All Exams --</option>
            {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </section>
        )}

        {selectedCourse && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold dark:text-white flex items-center gap-2">
                <ClipboardCheck size={20} className="text-emerald-500" /> Online Exams
              </h2>
              <div className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-lg text-xs font-bold uppercase">
                {courseExams.length} Published
              </div>
            </div>

            <div className="card p-6 space-y-6">
              {courseExams.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {courseExams.map((exam) => (
                    <div key={exam.id} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900 dark:text-white truncate">{exam.title}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {exam.questions.length} questions • {exam.duration_minutes} min • Pass {exam.passing_score}%
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleEditExam(exam)} className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDeleteExam(exam.id)} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  className="md:col-span-1 px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-sm"
                  placeholder="Exam title"
                  value={examTitle}
                  onChange={(e) => setExamTitle(e.target.value)}
                />
                <input
                  className="md:col-span-1 px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-sm"
                  placeholder="Category (Banking, SSC, etc.)"
                  list="exam-category-suggestions"
                  value={examCategory}
                  onChange={(e) => setExamCategory(e.target.value)}
                />
                <datalist id="exam-category-suggestions">
                  <option value="Banking" />
                  <option value="SSC" />
                  <option value="Railway" />
                  <option value="UPSC" />
                  <option value="Insurance" />
                  <option value="State Exams" />
                </datalist>
                <div className="grid grid-cols-2 gap-3 md:col-span-1">
                  <input
                    type="number"
                    min="1"
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-sm"
                    placeholder="Duration (min)"
                    value={examDuration}
                    onChange={(e) => setExamDuration(e.target.value)}
                  />
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-sm"
                    placeholder="Pass %"
                    value={examPassingScore}
                    onChange={(e) => setExamPassingScore(e.target.value)}
                  />
                </div>
              </div>

              {/* Group/Section management removed as requested - handled directly in question form */}

              <div className="flex flex-col md:flex-row gap-4 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/30 p-5 rounded-2xl border border-indigo-100/80 dark:border-indigo-800/50 shadow-sm">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
                    <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
                      <Sparkles size={16} fill="currentColor" className="text-indigo-600 dark:text-indigo-300" />
                    </div>
                    <div>
                      <h3 className="font-black text-sm tracking-tight">AI Question Builder</h3>
                      <p className="text-[10px] opacity-75 font-bold uppercase">Generate curriculum-aligned questions instantly</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Row 1: Core Context Identifiers */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="relative">
                        <input
                          list="ai-module-list"
                          className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-800 rounded-xl text-xs font-bold outline-none focus:ring-2 ring-indigo-500/50 placeholder:text-slate-400"
                          placeholder="Target Module Name"
                          value={aiModule}
                          onChange={(e) => setAiModule(e.target.value)}
                        />
                        <datalist id="ai-module-list">
                          {curriculum.map(m => <option key={m.id} value={m.title} />)}
                        </datalist>
                      </div>

                      <div className="relative">
                        <input
                          list="ai-section-list"
                          className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-800 rounded-xl text-xs font-bold outline-none focus:ring-2 ring-indigo-500/50 placeholder:text-slate-400"
                          placeholder="Target Section / Lesson Name"
                          value={aiSection}
                          onChange={(e) => setAiSection(e.target.value)}
                        />
                        <datalist id="ai-section-list">
                          {curriculum.filter(c => !aiModule || c.title === aiModule).flatMap(c => c.lessons || []).map(l => <option key={l.id} value={l.title} />)}
                        </datalist>
                      </div>
                    </div>

                    {/* Row 2: The EXPANDED Textarea Context Area */}
                    <div>
                      <textarea
                        rows="3"
                        className="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-800 rounded-xl text-sm outline-none focus:ring-2 ring-indigo-500/50 resize-none placeholder:text-slate-400"
                        placeholder="Paste full topic paragraphs, definitions, or specific focus keywords for the AI to use as foundation here..."
                        value={aiTopic}
                        onChange={(e) => setAiTopic(e.target.value)}
                      />
                    </div>

                    {/* Row 3: Operational Actions & Configs */}
                    <div className="flex flex-wrap sm:flex-nowrap items-center justify-end gap-3 pt-1">
                      <div className="flex-1 hidden sm:block"></div>
                      
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-black text-slate-500 uppercase">AI Model</span>
                        <select
                          className="px-3 py-2 bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-800 rounded-lg text-xs outline-none font-bold cursor-pointer"
                          value={aiProvider}
                          onChange={(e) => setAiProvider(e.target.value)}
                        >
                          <option value="nvidia">NVIDIA (Free)</option>
                          <option value="openai">ChatGPT</option>
                          <option value="gemini">Gemini</option>
                        </select>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-black text-slate-500 uppercase">Type</span>
                        <select
                          className="px-3 py-2 bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-800 rounded-lg text-xs outline-none font-bold cursor-pointer"
                          value={aiQuestionType}
                          onChange={(e) => setAiQuestionType(e.target.value)}
                        >
                          <option value="Multiple Choice Question">Multiple Choice</option>
                          <option value="True/False">True/False</option>
                          <option value="Fill in the Blanks">Fill in the Blanks</option>
                          <option value="Short Answer">Short Answer</option>
                        </select>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-black text-slate-500 uppercase">Cognitive</span>
                        <select
                          className="px-3 py-2 bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-800 rounded-lg text-xs outline-none font-bold cursor-pointer"
                          value={aiCognitiveLevel}
                          onChange={(e) => setAiCognitiveLevel(e.target.value)}
                        >
                          <option value="Recall">Recall</option>
                          <option value="Comprehension">Comprehension</option>
                          <option value="Application">Application</option>
                          <option value="Analysis">Analysis</option>
                          <option value="Evaluation">Evaluation</option>
                        </select>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-black text-slate-500 uppercase">Complexity</span>
                        <select
                          className="px-3 py-2 bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-800 rounded-lg text-xs outline-none font-bold cursor-pointer"
                          value={aiDifficulty}
                          onChange={(e) => setAiDifficulty(e.target.value)}
                        >
                          <option value="Easy">Easy</option>
                          <option value="Medium">Medium</option>
                          <option value="Hard">Hard</option>
                        </select>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-black text-slate-500 uppercase">Limit</span>
                        <input
                          type="number"
                          min="1"
                          max="50"
                          className="w-14 px-2 py-2 bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-800 rounded-lg text-xs outline-none text-center font-bold"
                          value={aiGenCount}
                          onChange={(e) => setAiGenCount(e.target.value)}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handleGenerateAIQuestions}
                        disabled={generatingAI}
                        className="px-6 py-2.5 bg-gradient-to-br from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-xl text-xs font-black transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-indigo-500/30 gap-2 ml-2"
                      >
                        {generatingAI ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} fill="currentColor" />}
                        BUILD NOW
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-indigo-100/50 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="text-[10px] text-indigo-600/70 font-bold flex items-center gap-1">
                      <FileText size={12} /> TEACHER REFERENCE TRAINING (RAG)
                    </div>
                    <label className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm shadow-indigo-500/20 transition-all">
                      <UploadCloud size={12} />
                      <span>Upload Previous Paper PDF to AI Brain</span>
                      <input type="file" accept=".pdf" className="hidden" onChange={handleSyncKnowledgeBasePDF} />
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-4 bg-sky-50 dark:bg-sky-900/20 p-4 rounded-xl border border-sky-100 dark:border-sky-800">
                <div className="flex-1">
                  <h3 className="font-bold text-sky-800 dark:text-sky-300 text-sm">Bulk Import Questions</h3>
                  <p className="text-xs text-slate-500 mt-1">Upload a CSV or Word (.txt/.docx) file containing multiple questions.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={downloadSampleCSV} className="text-xs font-bold px-3 py-2 bg-white dark:bg-slate-800 border rounded flex items-center gap-1 hover:bg-slate-50"><Download size={14} /> CSV Format</button>
                  <button type="button" onClick={downloadSampleWordText} className="text-xs font-bold px-3 py-2 bg-white dark:bg-slate-800 border rounded flex items-center gap-1 hover:bg-slate-50"><Download size={14} /> Word/TXT Format</button>
                  <label className="text-xs font-bold px-4 py-2 bg-sky-600 text-white rounded cursor-pointer hover:bg-sky-700">
                    Upload File
                    <input type="file" accept=".csv,.txt,.docx" className="hidden" onChange={handleBulkUpload} />
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <div className="p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3 relative bg-slate-50/50 dark:bg-slate-900/50">
                  <div className="absolute -top-3 left-4 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 text-[10px] font-black uppercase px-2 py-1 rounded">
                    {editingQuestionIndex !== null ? "Editing Question" : "New Question"}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2">
                    <div className="relative">
                      <input
                        list="group-list"
                        className="w-full px-4 py-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-sm focus:ring-2 focus:ring-emerald-500/20"
                        placeholder="Group Name"
                        value={currentQuestion.group_name}
                        onChange={(e) => updateCurrentQuestion("group_name", e.target.value)}
                      />
                      <datalist id="group-list">
                        {examGroups.map(g => <option key={g} value={g} />)}
                      </datalist>
                    </div>
                    <div className="relative">
                      <input
                        list="section-list"
                        className="w-full px-4 py-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-sm focus:ring-2 focus:ring-emerald-500/20"
                        placeholder="Section Name"
                        value={currentQuestion.section_name}
                        onChange={(e) => updateCurrentQuestion("section_name", e.target.value)}
                      />
                      <datalist id="section-list">
                        {(examSections[currentQuestion.group_name] || []).map(s => <option key={s} value={s} />)}
                      </datalist>
                    </div>
                    <input
                      className="px-4 py-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-sm focus:ring-2 focus:ring-emerald-500/20"
                      placeholder="Marks"
                      type="number"
                      value={currentQuestion.marks}
                      onChange={(e) => updateCurrentQuestion("marks", Number(e.target.value))}
                    />
                    <input
                      className="px-4 py-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-sm focus:ring-2 focus:ring-emerald-500/20"
                      placeholder="Negative marks"
                      type="number"
                      step="0.25"
                      value={currentQuestion.negative_marks}
                      onChange={(e) => updateCurrentQuestion("negative_marks", Number(e.target.value))}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2">
                    <select
                      className="px-4 py-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-sm focus:ring-2 focus:ring-emerald-500/20"
                      value={currentQuestion.question_type}
                      onChange={(e) => updateCurrentQuestion("question_type", e.target.value)}
                    >
                      <option value="Multiple Choice Question">Multiple Choice Question</option>
                      <option value="True/False">True/False</option>
                      <option value="Fill in the Blanks">Fill in the Blanks</option>
                      <option value="Short Answer">Short Answer</option>
                      <option value="Subjective Question">Subjective Question</option>
                    </select>
                    <label className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-emerald-600 bg-slate-100 border-slate-300 rounded focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:ring-offset-slate-800 dark:bg-slate-700 dark:border-slate-600"
                        checked={currentQuestion.is_mandatory !== false}
                        onChange={(e) => updateCurrentQuestion("is_mandatory", e.target.checked)}
                      />
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Mandatory</span>
                    </label>
                    <label className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-emerald-600 bg-slate-100 border-slate-300 rounded focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:ring-offset-slate-800 dark:bg-slate-700 dark:border-slate-600"
                        checked={currentQuestion.cannot_edit_response || false}
                        onChange={(e) => updateCurrentQuestion("cannot_edit_response", e.target.checked)}
                      />
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Cannot Edit Response</span>
                    </label>
                    <label className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-emerald-600 bg-slate-100 border-slate-300 rounded focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:ring-offset-slate-800 dark:bg-slate-700 dark:border-slate-600"
                        checked={currentQuestion.cannot_revisit || false}
                        onChange={(e) => updateCurrentQuestion("cannot_revisit", e.target.checked)}
                      />
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Cannot Revisit</span>
                    </label>
                    <label className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-emerald-600 bg-slate-100 border-slate-300 rounded focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:ring-offset-slate-800 dark:bg-slate-700 dark:border-slate-600"
                        checked={currentQuestion.allow_calculator || false}
                        onChange={(e) => updateCurrentQuestion("allow_calculator", e.target.checked)}
                      />
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-300">Allow Calculator</span>
                    </label>
                  </div>

                  <RichExamField
                    label="Linked Answer Passage (Optional)"
                    value={currentQuestion.linked_prompt || ""}
                    onChange={(value) => updateCurrentQuestion("linked_prompt", value)}
                    onUpload={(file, kind) => handleExamContentUpload(file, kind, null, "linked_prompt")}
                  />

                  <RichExamField
                    label="Question Prompt"
                    value={currentQuestion.prompt}
                    onChange={(value) => updateCurrentQuestion("prompt", value)}
                    onUpload={(file, kind) => handleExamContentUpload(file, kind, null, "prompt")}
                  />

                  <div className="w-full">
                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Explanation / Rationale</label>
                    <textarea
                      className="w-full px-4 py-3 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-sm focus:ring-2 focus:ring-emerald-500/20 placeholder:text-slate-400"
                      rows="3"
                      placeholder="Explain why the correct answer is right and the others are wrong..."
                      value={currentQuestion.explanation || ""}
                      onChange={(e) => updateCurrentQuestion("explanation", e.target.value)}
                    />
                  </div>

                  {["Multiple Choice Question", "True/False"].includes(currentQuestion.question_type) && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300">Options</h4>
                        <button
                          type="button"
                          onClick={() => {
                            const newId = String.fromCharCode(65 + (currentQuestion.options || []).length);
                            updateCurrentQuestion("options", [...(currentQuestion.options || []), { id: newId, text: "" }]);
                          }}
                          className="px-3 py-1 text-xs font-bold bg-slate-200 dark:bg-slate-800 rounded hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors"
                        >
                          + Add Option
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(currentQuestion.options || []).map((opt, optionIndex) => (
                          <div key={optionIndex} className="relative p-3 border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-950">
                            <div className="flex gap-2 mb-2">
                              <input
                                className="w-16 px-2 py-1 text-xs border rounded outline-none dark:bg-slate-900 dark:border-slate-800"
                                value={opt.id}
                                onChange={(e) => {
                                  updateCurrentQuestionOption(optionIndex, "id", e.target.value);
                                }}
                                placeholder="Label"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setCurrentQuestion(prev => {
                                    const newOpts = (prev.options || []).filter((_, idx) => idx !== optionIndex);
                                    return { ...prev, options: newOpts };
                                  });
                                }}
                                className="text-[10px] text-red-500 font-bold px-2 border border-red-200 bg-red-50 hover:bg-red-100 dark:border-red-900/30 dark:bg-red-900/20 dark:hover:bg-red-900/40 rounded uppercase tracking-wider ml-auto"
                              >
                                Remove
                              </button>
                            </div>
                            <RichExamField
                              label={`Option Text`}
                              value={opt.text}
                              onChange={(value) => {
                                updateCurrentQuestionOption(optionIndex, "text", value);
                              }}
                              onUpload={async (file, kind) => {
                                if (!file) return;
                                const loadingToast = toast.loading(`Uploading ${kind}...`);
                                try {
                                  const fallbackType = kind === "image" ? "image/jpeg" : "video/mp4";
                                  const contentType = file.type || fallbackType;
                                  const formData = new FormData();
                                  formData.append("file", file);
                                  const res = await api.post("/upload/file", formData);
                                  const file_url = res.data.url;
                                  let extraStyles = "max-width:100%;border-radius:8px;margin:8px 0;";
                                  if (kind === "image") {
                                    const customWidth = window.prompt("Enter image width (e.g. 200px, 50%, or leave blank for default):");
                                    if (customWidth) extraStyles += `width:${customWidth};`;
                                    const customHeight = window.prompt("Enter image height (e.g. 200px, auto, or leave blank for default):");
                                    if (customHeight) extraStyles += `height:${customHeight};`;
                                  }

                                  const tag = kind === "image"
                                    ? `<img src="${file_url}" alt="${file.name}" style="${extraStyles}" />`
                                    : `<video src="${file_url}" controls controlsList="nodownload" style="${extraStyles}"></video>`;
                                  setCurrentQuestion(prev => {
                                    const newOpts = [...(prev.options || [])];
                                    newOpts[optionIndex] = { ...newOpts[optionIndex], text: (newOpts[optionIndex].text || "") + `\n${tag}` };
                                    return { ...prev, options: newOpts };
                                  });
                                  toast.dismiss(loadingToast);
                                  toast.success(`${kind === "image" ? "Image" : "Video"} added`);
                                } catch (err) {
                                  toast.dismiss(loadingToast);
                                  toast.error("Upload failed");
                                }
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      <select
                        className="px-4 py-2.5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none text-sm focus:ring-2 focus:ring-emerald-500/20 w-full"
                        value={currentQuestion.correct_option}
                        onChange={(e) => updateCurrentQuestion("correct_option", e.target.value)}
                      >
                        <option value="">Select Correct Option</option>
                        {(currentQuestion.options || []).map((opt) => (
                          <option key={opt.id} value={opt.id}>Correct answer: {opt.id}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="flex gap-3 pt-3">
                    <button onClick={handleSaveCurrentQuestion} className="btn-secondary flex-1 py-2.5 flex items-center justify-center gap-2 border-emerald-200 dark:border-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20">
                      <Plus size={16} /> {editingQuestionIndex !== null ? "Update Question in List" : "Save Question to List"}
                    </button>
                    {editingQuestionIndex !== null && (
                      <button onClick={() => { setEditingQuestionIndex(null); setCurrentQuestion(createBlankExamQuestion(activeExamGroup, activeExamSection)); }} className="flex-1 py-2.5 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-white rounded-xl font-bold transition-colors">
                        Cancel Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {examQuestions.length > 0 && (
                <div className="space-y-3 mt-8 border-t border-slate-200 dark:border-slate-800 pt-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-500 uppercase">Saved Questions in Exam ({examQuestions.length})</h3>
                    <button
                      onClick={() => { if (window.confirm("Remove all questions from this exam list?")) setExamQuestions([]); }}
                      className="text-xs bg-red-50 hover:bg-red-100 text-red-600 dark:bg-red-900/20 dark:hover:bg-red-900/30 border border-red-100 dark:border-red-900/30 px-2.5 py-1 rounded-lg font-bold transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                  {examQuestions.map((q, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-3 bg-white dark:bg-slate-900 border ${editingQuestionIndex === idx ? 'border-blue-500 shadow-sm shadow-blue-500/20' : 'border-slate-200 dark:border-slate-800'} rounded-xl`}>
                      <div className="flex-1 min-w-0 pr-4">
                        <div className="text-sm font-bold text-slate-900 dark:text-white truncate">Q{idx + 1}: <span dangerouslySetInnerHTML={{ __html: q.prompt.replace(/<[^>]+>/g, '').substring(0, 50) + '...' }} /></div>
                        <div className="text-xs text-slate-500 mt-1">{q.group_name} • {q.section_name} • {q.marks} Marks • Correct: {q.correct_option}</div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => editQuestionFromList(idx)} className="text-blue-600 p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"><Edit2 size={16} /></button>
                        <button onClick={() => removeExamQuestion(idx)} className="text-red-500 p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">

                {editingExamId && (
                  <button onClick={cancelEdit} className="flex-1 py-2.5 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-900 dark:text-white rounded-xl font-bold flex items-center justify-center gap-2">
                    <X size={16} /> Cancel
                  </button>
                )}
                <button
                  onClick={handleCreateExam}
                  disabled={selectedCourse === "all" || !selectedCourse}
                  className={`flex-1 py-2.5 rounded-xl font-bold flex items-center justify-center gap-2 ${selectedCourse === "all" || !selectedCourse ? 'bg-slate-300 cursor-not-allowed text-slate-500' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
                >
                  <ClipboardCheck size={16} /> {editingExamId ? "Update Exam" : "Publish Exam"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
